import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    Signal,
    signal,
    untracked
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngxs/store';
import { distinctUntilChanged, filter, take, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { ImpersonationService } from './admin/impersonation/impersonation.service';
import {
    adminRoute,
    authRoute,
    brandBuilderRoute,
    ethicalSelfReflectionRoute,
    forbiddenRoute,
    growthTalksRoute,
    selfReflectionJourneyRoute
} from './app.routes';
import { leadersRoute } from './leaders/leaders.routes';
import { ImpersonationBarComponent } from './shared/components/impersonation-bar/impersonation-bar.component';
import { LoadingIndicatorComponent } from './shared/components/loading-indicator/loading-indicator.component';
import { MYG_LANGUAGE_CODE } from './shared/enums/language-code.enum';
import { TRACKING_ITEM } from './shared/enums/tracking_item.enum';
import { IUserConfig } from './shared/interfaces/IUserConfig';
import { IUserData } from './shared/interfaces/IUserData';
import { DocumentPreviewService } from './shared/services/document-preview.service';
import { HrefInterceptorService } from './shared/services/href-interceptor.service';
import { LanguageService } from './shared/services/language.service';
import { LoadingIndicatorService } from './shared/services/loading-indicator.service';
import { TrackingService } from './shared/services/tracking.service';
import {
    GetUserConfig,
    GetUserScdData
} from './shared/state-management/user/user.actions';
import { UserState } from './shared/state-management/user/user.state';
import { SurveyEligibilityService } from './shared/survey/services/survey-eligibility.service';
import { SurveyStorageService } from './shared/survey/services/survey-storage.service';
import { SurveyService } from './shared/survey/services/survey.service';
import { SetShowSurveyButton } from './shared/survey/state/survey.actions';
import { Language } from './shared/types/language.type';
import { SetSearchVisibility } from './static/header/states/header-search.actions';
import { HeaderSearchState } from './static/header/states/header-search.state';

@Component({
    selector: 'gt-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [LoadingIndicatorComponent, ImpersonationBarComponent, RouterOutlet],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent {
    protected readonly impersonation = inject(ImpersonationService);
    private readonly translate = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly tracking = inject(TrackingService);
    private readonly loadingIndicator = inject(LoadingIndicatorService);
    private readonly hrefInterceptor = inject(HrefInterceptorService);
    private readonly documentPreview = inject(DocumentPreviewService);
    private readonly store = inject(Store);
    private readonly destroyRef = inject(DestroyRef);
    private readonly languageService = inject(LanguageService);
    private readonly surveyService = inject(SurveyService);
    private readonly surveyStorageService = inject(SurveyStorageService);
    private readonly surveyEligibilityService = inject(SurveyEligibilityService);

    protected readonly userData: Signal<IUserData> = select(UserState.loggedUser);
    protected readonly userConfig: Signal<IUserConfig> = select(UserState.userConfig);
    private readonly searchOpen: Signal<boolean> = select(HeaderSearchState.getSearchOpenState);

    protected readonly languagesSignal: Signal<Language[]> = this.languageService.languagesSignal;
    protected readonly showLoadingIndicator = computed(() => this.loadingIndicator.showLoadingIndicator());

    private readonly isAdminRoute = computed(() => this.currentUrl()?.startsWith(`/${adminRoute}`));

    private readonly mainRouteSegment = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => this.getMainRouteSegment(event.urlAfterRedirects)),
            distinctUntilChanged()
        )
    );

    private readonly routeChange = toSignal(
        this.router.events.pipe(
            filter(
                (event): event is NavigationEnd =>
                    event instanceof NavigationEnd && !event.url.startsWith(`/${authRoute}`)
            ),
            map((event) => this.tracking.cleanRoute(event.urlAfterRedirects)),
            distinctUntilChanged()
        )
    );

    private readonly currentUrl = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => event.urlAfterRedirects),
            distinctUntilChanged()
        )
    );

    private readonly currentAsset = toSignal(this.route.queryParams.pipe(map((queryParams) => queryParams['asset'])));

    protected showMaintenancePage = signal(false);

    constructor() {
        this.routeNavigationEndEffect();
        this.routeSegmentChangeEffect();
        this.routeRecognizedEffect();
        this.currentAssetEffect();
        this.setupSurveyEffect();
        this.hrefInterceptor.initialise();
        this.translate.setDefaultLang(MYG_LANGUAGE_CODE.EN);

        // Load user config after auth data has been initialized (via APP_INITIALIZER)
        effect(() => {
            const loggedUser = this.userData();

            if (loggedUser?.gid) {
                this.store.dispatch(new GetUserConfig());
            }
        });
    }

    private closeSearch(): void {
        if (this.searchOpen()) {
            this.store.dispatch(new SetSearchVisibility(false));
        }
    }

    private setupSurveyEffect(): void {
        effect(() => {
            const isAdmin = this.isAdminRoute();
            const url = this.currentUrl();
            const userData = this.userData();
            const isAuthenticated = !!userData?.gid;

            if (!url || isAdmin || !isAuthenticated) {
                return;
            }

            const userInfo = this.surveyStorageService.getUserInfo();

            if (userInfo) {
                this.handleExistingUserInfo(userInfo.costCenterLocation);
            } else {
                this.fetchAndHandleUserInfo();
            }
        });
    }

    private handleExistingUserInfo(costCenter: string): void {
        if (this.surveyEligibilityService.isValidCostCenter(costCenter)) {
            this.surveyService
                .handleSurvey(this.showMaintenancePage())
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe();
        } else {
            this.store.dispatch(new SetShowSurveyButton(false));
        }
    }

    private fetchAndHandleUserInfo(): void {
        this.store
            .dispatch(new GetUserScdData())
            .pipe(
                tap(() => {
                    const scdData = this.store.selectSnapshot(UserState.scdData);
                    const costCenter = scdData?.costLocationUnit;

                    if (!costCenter) {
                        this.store.dispatch(new SetShowSurveyButton(false));
                        return;
                    }

                    this.surveyStorageService.setUserInfo(costCenter);

                    if (this.surveyEligibilityService.isValidCostCenter(costCenter)) {
                        this.surveyService
                            .handleSurvey(this.showMaintenancePage())
                            .pipe(takeUntilDestroyed(this.destroyRef))
                            .subscribe();
                    } else {
                        this.store.dispatch(new SetShowSurveyButton(false));
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private routeRecognizedEffect(): void {
        effect(() => {
            const change = this.routeChange();

            if (change) {
                this.tracking.addTrackingItem(TRACKING_ITEM.ROUTE, change);
            }
        });
    }

    private routeNavigationEndEffect(): void {
        effect(() => {
            const currentUrl = this.currentUrl();

            untracked(() => {
                this.setDocumentTitle(currentUrl);
                this.closeSearch();

                window['ste_statistic'] = window['ste_statistic'] || [];
                window['ste_statistic'].push({
                    action: 'tracking.started',
                    callback: () => {
                        const params = { ...this.route.snapshot.queryParams };
                        delete params.sitc;
                    }
                });
            });
        });
    }

    private routeSegmentChangeEffect(): void {
        effect(() => {
            const mainSegment = this.mainRouteSegment();

            if (mainSegment) {
                window.scrollTo({ top: 0, behavior: 'auto' });
            }
        });
    }

    private getMainRouteSegment(url: string): string {
        const segments = url.split('/').filter((segment) => segment);
        return segments[0] || '';
    }

    private currentAssetEffect(): void {
        effect(() => {
            const assetId = this.currentAsset();

            if (assetId && !this.router.url.includes(`/${growthTalksRoute}/resources`)) {
                this.documentPreview.assetPreview(assetId).pipe(take(1)).subscribe();
            }
        });
    }

    private setDocumentTitle(currentUrl: string): void {
        if (currentUrl?.startsWith(`/${forbiddenRoute}`)) {
            document.title = 'Access denied';
        } else if (currentUrl?.startsWith(`/${brandBuilderRoute}`)) {
            document.title = 'My Brand Builder';
        } else if (currentUrl?.startsWith(`/${leadersRoute}`)) {
            document.title = 'MyGrowth Leaders';
        } else if (currentUrl?.startsWith(`/${selfReflectionJourneyRoute}`)) {
            document.title = 'Self-Reflection Journey';
        } else if (currentUrl?.startsWith(`/${ethicalSelfReflectionRoute}`)) {
            document.title = 'Ethical Self-Reflection';
        } else {
            document.title = 'Siemens MyGrowth';
        }
    }
}
